import { ThunkAction } from 'redux-thunk'
import { AnyAction } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import { ChainId } from '@nominex/nomiswap-sdk'
import { DeserializedFarmConfig, SerializedFarmConfig } from 'config/farms'
import { DeserializedPoolConfig, SerializedPoolConfig } from 'config/pools'
import {
  AffiliateResponseActiveReferrals,
  AffiliateTeam,
  Distribution,
  FarmHolderBonusPoolInfo,
  ReferralsStatistics,
  TotalReward,
} from 'restApi/openapi'

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, State, unknown, AnyAction>

export type SerializedBigNumber = string

interface SerializedFarmUserData {
  allowance: string
  tokenBalance: string
  stakedBalance: string
  earnings: string
}

export interface DeserializedFarmUserData {
  allowance: BigNumber
  tokenBalance: BigNumber
  stakedBalance: BigNumber
  earnings: BigNumber
}

export type HolderBonusPool = Omit<FarmHolderBonusPoolInfo, 'farmAddress'>

export interface SerializedFarm extends SerializedFarmConfig {
  holderBonusPool?: HolderBonusPool
  tokenPriceBusd?: string
  quoteTokenPriceBusd?: string
  earningTokenPriceBusd?: string
  lpTokenPriceBusd?: string
  tokenAmountTotal?: SerializedBigNumber
  quoteTokenAmountTotal?: SerializedBigNumber
  lpTotalInQuoteToken?: SerializedBigNumber
  lpTotalSupply?: SerializedBigNumber
  tokenPriceVsQuote?: SerializedBigNumber
  tokensPerSec?: SerializedBigNumber
  userData?: SerializedFarmUserData
}

export interface DeserializedFarm extends Omit<DeserializedFarmConfig, 'contractAddress'> {
  holderBonusPool?: HolderBonusPool
  tokenPriceBusd?: string
  quoteTokenPriceBusd?: string
  earningTokenPriceBusd?: string
  lpTokenPriceBusd?: string
  tokenAmountTotal?: BigNumber
  quoteTokenAmountTotal?: BigNumber
  lpTotalInQuoteToken?: BigNumber
  lpTotalSupply?: BigNumber
  tokenPriceVsQuote?: BigNumber
  tokensPerSec?: BigNumber
  userData?: DeserializedFarmUserData
}

export interface FarmWithStakedValue extends DeserializedFarm {
  baseApr?: number | null
  holderBonusApr?: number | null
  lpRewardsApr?: number | null
  cumulativeApr?: number | null
  apy?: number | null
  liquidity?: BigNumber
}

interface CorePoolProps {
  apr?: number
  stakingTokenPrice?: number
  earningTokenPrice?: number
  isAutoVault?: boolean
}

export interface BoostingInfo {
  boostingRate: number
  duration: number
  historicalBoostingRate: BigNumber
  historicalCompoundRate: BigNumber
  paused: boolean
  penaltyRate: number
  totalShares: BigNumber
  totalStakedCompounded: BigNumber
  totalBoostings: BigNumber
  unstakedShares?: BigNumber
}

interface SerializedBoostingInfo {
  boostingRate: number
  duration: number
  historicalBoostingRate: SerializedBigNumber
  historicalCompoundRate: SerializedBigNumber
  paused: boolean
  penaltyRate: number
  totalShares: SerializedBigNumber
  totalStakedCompounded: SerializedBigNumber
  totalBoostings: SerializedBigNumber
  unstakedShares: SerializedBigNumber
}

export interface UserBoostingInfo {
  amount: BigNumber
  boostingAmount: BigNumber
  initialBoostingRate: BigNumber
  initialCompoundRate: BigNumber
  principalAmount: BigNumber
  shares: BigNumber
  stakedAt: BigNumber
  unlockedBoostingAmount: BigNumber
}

export interface SerializedUserBoostingInfo {
  amount: SerializedBigNumber
  boostingAmount: SerializedBigNumber
  initialBoostingRate: SerializedBigNumber
  initialCompoundRate: SerializedBigNumber
  principalAmount: SerializedBigNumber
  shares: SerializedBigNumber
  stakedAt: SerializedBigNumber
  unlockedBoostingAmount: SerializedBigNumber
}

export interface DeserializedPool extends DeserializedPoolConfig, CorePoolProps {
  boostingInfo?: BoostingInfo
  holderBonusPool?: HolderBonusPool
  totalStaked?: BigNumber
  userData?: {
    boostingInfo?: UserBoostingInfo
    allowance: BigNumber
    stakingTokenBalance: BigNumber
    stakedBalance: BigNumber
    pendingReward: BigNumber
  }
}

export interface SerializedPool extends SerializedPoolConfig, CorePoolProps {
  boostingInfo?: SerializedBoostingInfo
  holderBonusPool?: HolderBonusPool
  totalStaked?: SerializedBigNumber
  userData?: {
    boostingInfo?: SerializedUserBoostingInfo
    allowance: SerializedBigNumber
    stakingTokenBalance: SerializedBigNumber
    stakedBalance: SerializedBigNumber
    pendingReward: SerializedBigNumber
  }
}

// Slices states

export interface SerializedFarmsState {
  chainId: ChainId
  data: SerializedFarm[]
  execKeys: Record<string, boolean>
  userDataLoaded: boolean
}

export interface DeserializedFarmsState {
  data: DeserializedFarm[]
  userDataLoaded: boolean
}

export interface PoolsState {
  data: SerializedPool[]
  userDataLoaded: boolean
}

export enum FetchStatus {
  NOT_FETCHED = 'not-fetched',
  FETCHING = 'fetching',
  FETCHED = 'fetched',
  FAILED = 'failed',
}

export interface AccountInfo {
  address: string
  distribution: Distribution
  referredBy: number
  referralId: number
  userId: number
}

export interface ProfileState {
  sessionFetchStatus: FetchStatus
  accountInfoFetchStatus: FetchStatus
  authentication: { address: string } | null
  accountInfo: AccountInfo
}

// Affiliate

export enum ReferralLevel {
  STARTER = 'STARTER',
  PARTNER = 'PARTNER',
  PRO = 'PRO',
  VIP = 'VIP',
  ELITE = 'ELITE',
  MAX = 'MAX',
}

export interface FarmingLevel {
  cashback: number
  name: ReferralLevel
  referralFarming: number
  referralTrading: number
  teamFarming: number
  teamTrading: number
  threshold: number
}

export type UsdInvestedInFarming = number
export type MinUsdAmountForNextLevel = number

export interface UserFarmingLevel {
  currentLevel: ReferralLevel
  nextLevel: Omit<ReferralLevel, ReferralLevel.STARTER>
  farmingLevelProgress: [UsdInvestedInFarming, MinUsdAmountForNextLevel]
}

export interface UserBonuses {
  cashback: number
  holderBonus: number
  referralFarming: number
  referralTrading: number
  teamFarming: number
  teamTrading: number
  total: number
}

export type RewardFactor = number
export type CurrentNumberOfDays = number
export type NextLevelNumberOfDays = number | null
export type NmxPerDay = number

export interface HolderBonusResponse {
  farmAddress: string
  currentBonus: RewardFactor
  nextBonus: RewardFactor | null
  holderBonusProgress: [CurrentNumberOfDays, NextLevelNumberOfDays]
  currentRewardInNmx: NmxPerDay
  poolShare: number
}

export interface HolderBonus extends Omit<HolderBonusResponse, 'holderBonusProgress'> {
  currentNumberOfDays: CurrentNumberOfDays
  nextLevelNumberOfDays: NextLevelNumberOfDays
  totalEarned: number
  unclaimed: number
}

export interface AffiliateState {
  farmingLevels: FarmingLevel[]
  user: {
    farmingLevel: {
      fetchStatus: FetchStatus
    } & UserFarmingLevel
    bonuses: {
      fetchStatus: FetchStatus
      nextReward: {
        fetchStatus: FetchStatus
      } & UserBonuses
      totalEarned: {
        fetchStatus: FetchStatus
      } & UserBonuses
    } & UserBonuses
    holderBonus: {
      fetchStatus: FetchStatus
      farms: HolderBonus[]
    }
    rewards: {
      totalEarned: {
        fetchStatus: FetchStatus
      } & TotalReward
    }
  }
}

// Referrals

export interface ReferralsState {
  fetchStatus: FetchStatus
  activeReferrals: AffiliateResponseActiveReferrals
  directReferrals: ReferralsStatistics
  leftTeam: AffiliateTeam
  rightTeam: AffiliateTeam
}

// Global state

export interface State {
  affiliate: AffiliateState
  farms: SerializedFarmsState
  pools: PoolsState
  profile: ProfileState
  referrals: ReferralsState
}
